import { useSelector } from "react-redux";
import { usersSelector } from "../../Reducers/usersReducer";
import styles from "./users-and-roles.module.css";
import { useCallback, useEffect, useMemo, useState } from "react";
import PlusIcon from "../Icons/plus-icon";
import { useCreateRoleMutation, useFetchRolesQuery, useUpdateRoleMutation } from "../../Apis/rolesApi";
import RolesTable from "./RolesTable";
import useListProcessing from "../../Hooks/useListProcessing";
import { genericSorter } from "../../Util/TableHelpers/commonHelper";
import RoleModal from "./RoleModal";

const processConfigs = {
  order: {
    SYSTEM_THEN_ROLE_ID: (a, b) => {
      const aKey = [a.role.isSystemRole() ? 0 : 1, a.role.getId()];
      const bKey = [b.role.isSystemRole() ? 0 : 1, b.role.getId()];
      return genericSorter(aKey, bKey);
    },
    _DEFAULT: {
      type: "SYSTEM_THEN_ROLE_ID",
      ascending: true,
    }
  }
}

const RoleSettings = ({setSuccessNotification, setErrorNotification}) => {
  const users = useSelector(usersSelector);
  const { data: roles } = useFetchRolesQuery();
  const [createRole, { isLoading: isCreating, isSuccess: createSuccess, isError: createError, reset: resetCreate }] = useCreateRoleMutation();
  const [updateRole, { isLoading: isUpdating, isSuccess: updateSuccess, isError: updateError, reset: resetUpdate }] = useUpdateRoleMutation();
  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState(null);

  const createRoleHandler = () => {
    setSelectedRoleId(null);
    setRoleModalOpen(true);
  }

  const editRole = useCallback((roleId) => {  
    setSelectedRoleId(roleId);
    setRoleModalOpen(true);
  }, []);
  
  const data = useMemo(() => {
    return roles.getRoles().map(role => {
      return {
        role: role,
        userCount: users.getUsers().filter(user => user.getRoleId() === role.getId()).length,
        edit: () => editRole(role.getId()),
      }
    })
  }, [users, roles, editRole]);

  useEffect(() => {
    if (createSuccess) {
      resetCreate();
      setRoleModalOpen(false);
      setSuccessNotification("Role created successfully");
    } else if (createError) {
      resetCreate();
      setErrorNotification("Failed to create role");
    }
  }, [createSuccess, createError, resetCreate, setSuccessNotification, setErrorNotification]);

  useEffect(() => {
    if (updateSuccess) {
      resetUpdate();
      setRoleModalOpen(false);
      setSuccessNotification("Role updated successfully");
    } else if (updateError) {
      resetUpdate();
      setErrorNotification("Failed to update role");
    }
  }, [updateSuccess, updateError, resetUpdate, setSuccessNotification, setErrorNotification]);

  const { processedData } = useListProcessing(data, processConfigs);

  return (
    <>
      <div className={styles.content}>
        <div className={styles.usersControls}>
          <div className={styles.usersControlsLeft}>
            <div className={styles.usersHeading}>
              <div className={styles.usersHeadingTitle}>Roles</div>
              <div className={styles.usersHeadingDescription}>
                Manage roles and permissions for users
              </div>
            </div>
          </div>
          <div className={styles.usersControlsRight}>
            <button
              className={styles.usersInviteButton}
              onClick={createRoleHandler}
            >
              <PlusIcon />
              <div className={styles.usersInviteButtonText}>Add new role</div>
            </button>
          </div>
        </div>
        <RolesTable data={processedData} />
      </div>

      <RoleModal
        open={roleModalOpen}
        onClose={() => setRoleModalOpen(false)}
        roleId={selectedRoleId}
        roles={roles}
        onCreate={createRole}
        onUpdate={updateRole}
        isCreating={isCreating}
        isUpdating={isUpdating}
      />
    </>
  );
};
export default RoleSettings;
